import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import PlatformSettingsLayout from '@/components/settings/platform/PlatformSettingsLayoutV2';
import { Panel } from '@ampm-7en-ai/ui/components/Panel';
import { Field, Input } from '@ampm-7en-ai/ui/components/Input';
import { Textarea } from '@ampm-7en-ai/ui/components/Textarea';
import { Select } from '@ampm-7en-ai/ui/components/Select';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Checkbox } from '@ampm-7en-ai/ui/components/Checkbox';
import { Pill } from '@ampm-7en-ai/ui/components/StatusBadge';
import { ToggleRow } from '@ampm-7en-ai/ui/components/ToggleRow';
import { Callout } from '@ampm-7en-ai/ui/components/Callout';
import { Skeleton } from '@ampm-7en-ai/ui/components/Skeleton';
import { PermissionProvider, usePermission } from '@/context/PermissionContext';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { API_ENDPOINTS, getApiUrl, getAuthHeaders, getRoleEndpoint } from '@/utils/api-config';

/**
 * Making a role, or changing one.
 *
 * Permissions come grouped by the feature they belong to, so the page shows
 * the groups as a list you tick rather than a master-detail pane: picking a
 * group is the decision, and seeing what it carries is the reason for it.
 */

interface Role {
  id: number;
  name: string;
  description: string;
  feature_ids?: number[];
  features?: { id: number; name: string; description: string }[];
  level?: number;
  is_active: boolean;
  created_at: string;
}

const LEVELS = [1, 2, 3, 4, 5].map((level) => ({ value: String(level), label: `Level ${level}` }));

/** The groups a role carries, from whichever field the server filled in. */
const featureIdsOf = (role: Role): number[] => {
  const direct = Array.isArray(role.feature_ids) ? role.feature_ids : [];
  if (direct.length) return Array.from(new Set(direct));
  return Array.from(
    new Set((role.features ?? []).map((feature) => feature?.id).filter((id): id is number => typeof id === 'number'))
  );
};

function RoleEditorContent() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getToken } = useAuth();
  const { availablePermissionGroups, isLoading: loadingPermissions } = usePermission();

  const editing = Boolean(roleId);

  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [level, setLevel] = React.useState('1');
  const [active, setActive] = React.useState(true);
  const [featureIds, setFeatureIds] = React.useState<number[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [failed, setFailed] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!editing || !roleId) return;

    const load = async () => {
      setLoading(true);
      setFailed(null);
      try {
        const token = getToken();
        if (!token) throw new Error('You need to be signed in to edit a role.');

        const response = await fetch(getApiUrl(getRoleEndpoint(Number(roleId))), {
          method: 'GET',
          headers: getAuthHeaders(token),
        });
        if (!response.ok) throw new Error('Couldn’t load this role.');

        const role: Role = (await response.json()).data;
        setName(role.name ?? '');
        setDescription(role.description ?? '');
        setLevel(LEVELS.some((one) => one.value === String(role.level)) ? String(role.level) : '1');
        setActive(role.is_active ?? true);
        setFeatureIds(featureIdsOf(role));
      } catch (error) {
        setFailed(error instanceof Error ? error.message : 'Couldn’t load this role.');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [editing, roleId, getToken]);

  // A group the server no longer offers shouldn't stay ticked invisibly.
  React.useEffect(() => {
    if (!availablePermissionGroups.length || !featureIds.length) return;
    const offered = new Set(availablePermissionGroups.map((group) => group.id));
    const kept = featureIds.filter((id) => offered.has(id));
    if (kept.length !== featureIds.length) setFeatureIds(kept);
  }, [availablePermissionGroups, featureIds]);

  const toggle = (featureId: number, on: boolean) =>
    setFeatureIds((current) => (on ? Array.from(new Set([...current, featureId])) : current.filter((id) => id !== featureId)));

  const permissionCount = React.useMemo(
    () =>
      availablePermissionGroups
        .filter((group) => featureIds.includes(group.id))
        .reduce((total, group) => total + (group.permissions?.length ?? 0), 0),
    [availablePermissionGroups, featureIds]
  );

  const blocked = !name.trim() || featureIds.length === 0;

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (blocked) return;

    setSaving(true);
    try {
      const token = getToken();
      if (!token) throw new Error('You need to be signed in to save a role.');

      const payload = { name, description, level: Number(level), feature_ids: featureIds, is_active: active };
      const response = await fetch(
        editing ? getApiUrl(getRoleEndpoint(Number(roleId))) : getApiUrl(API_ENDPOINTS.USER_ROLE),
        { method: editing ? 'PUT' : 'POST', headers: getAuthHeaders(token), body: JSON.stringify(payload) }
      );
      if (!response.ok) throw new Error(`The role wasn’t ${editing ? 'updated' : 'created'}.`);

      toast({ title: `${name} ${editing ? 'updated' : 'created'}` });
      navigate('/v2/settings/platform/security');
    } catch (error) {
      toast({
        title: 'That didn’t work',
        description: error instanceof Error ? error.message : 'The role wasn’t saved.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PlatformSettingsLayout
      title={editing ? 'Edit role' : 'New role'}
      description="A role is a set of permissions. People get everything theirs carries."
    >
      <Button variant="ghost" className="-ml-2 mb-3 gap-1.5" onClick={() => navigate('/v2/settings/platform/security')}>
        <ArrowLeft /> All roles
      </Button>

      {failed && (
        <Callout tone="danger" className="mb-4" title="Couldn’t load this role">
          {failed}
        </Callout>
      )}

      <form onSubmit={save}>
        <Panel padding="md">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" htmlFor="role-name">
              <Input
                id="role-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Support lead"
                disabled={loading}
              />
            </Field>
            <Field label="Level" htmlFor="role-level" hint="Higher levels outrank lower ones.">
              <Select id="role-level" options={LEVELS} value={level} onValueChange={setLevel} disabled={loading} />
            </Field>
          </div>

          <Field label="Description" htmlFor="role-description" className="mt-4" hint="What this role is for, in a line.">
            <Textarea
              id="role-description"
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Handles escalations and can see every conversation."
              disabled={loading}
            />
          </Field>

          <ToggleRow
            className="mt-4"
            label="Active"
            description="An inactive role can’t be handed out, and anyone holding it keeps nothing."
            checked={active}
            onCheckedChange={setActive}
            disabled={loading}
          />
        </Panel>

        <Panel padding="md" className="mt-4">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
            <div>
              <h2 className="text-ui-base font-medium text-ui-fg">What it can do</h2>
              <p className="mt-0.5 text-ui-xs text-ui-fg-muted">
                Tick the areas this role covers. Each one brings its own permissions.
              </p>
            </div>
            {featureIds.length > 0 && (
              <Pill tone="accent">
                {featureIds.length} {featureIds.length === 1 ? 'area' : 'areas'} · {permissionCount} permissions
              </Pill>
            )}
          </div>

          {loadingPermissions || loading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }, (_, index) => (
                <Skeleton key={index} className="h-16 w-full rounded-ui-md" />
              ))}
            </div>
          ) : availablePermissionGroups.length === 0 ? (
            <p className="text-ui-sm text-ui-fg-muted">No permission areas are defined yet.</p>
          ) : (
            <ul className="space-y-2">
              {availablePermissionGroups.map((group) => {
                const on = featureIds.includes(group.id);
                return (
                  <li
                    key={group.id}
                    className={`rounded-ui-md border p-3 transition-colors ${
                      on ? 'border-ui-accent bg-ui-accent/5' : 'border-ui-border'
                    }`}
                  >
                    <Checkbox
                      checked={on}
                      onChange={(event) => toggle(group.id, event.target.checked)}
                      label={<span className="text-ui-sm font-medium text-ui-fg">{group.name}</span>}
                      description={
                        <span className="mt-1 block">
                          {group.description && (
                            <span className="block text-ui-xs text-ui-fg-muted">{group.description}</span>
                          )}
                          {(group.permissions?.length ?? 0) > 0 && (
                            <span className="mt-1.5 flex flex-wrap gap-1.5">
                              {group.permissions.map((permission) => (
                                <Pill key={permission.id ?? permission.name}>{permission.name}</Pill>
                              ))}
                            </span>
                          )}
                        </span>
                      }
                    />
                  </li>
                );
              })}
            </ul>
          )}

          {!loading && !loadingPermissions && featureIds.length === 0 && (
            <p className="mt-3 text-ui-xs text-ui-fg-muted">
              A role with no areas can’t do anything, so at least one is needed.
            </p>
          )}
        </Panel>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => navigate('/v2/settings/platform/security')}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={saving || loading || blocked}>
            {saving ? 'Saving…' : editing ? 'Save role' : 'Create role'}
          </Button>
        </div>
      </form>
    </PlatformSettingsLayout>
  );
}

export default function RoleEditorV2() {
  return (
    <PermissionProvider>
      <RoleEditorContent />
    </PermissionProvider>
  );
}
